import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Select,
  Input,
  Typography,
  Space,
  Row,
  Col,
  Tag,
  Checkbox,
  Button,
} from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { useGrades } from "../../../../hooks/useGrades";
import { usePrograms } from "../../../../hooks/usePrograms";
import { useSubjects } from "../../../../hooks/useSubject";
import { SelectionCell } from "../../../../services/classrooms/classrooms.types";
import {
  buildSubjectOptions,
  countGenerationDelta,
  areAllSubjectsSelected,
  resolveAllSubjectsToggle,
  ALL_SUBJECTS_VALUE,
  restrictToSchoolPrograms,
  DEFAULT_GROUP_NAME,
  nextGroupName,
  lockedGroupsForGrade,
  unionGroupNames,
  DeltaClass,
} from "./SectionGradeProgramGrid.util";

const { Title, Text } = Typography;

type Section = "preescolar" | "primaria";

const SECTION_LABEL: Record<Section, string> = {
  preescolar: "Preescolar",
  primaria: "Primaria",
};

const SECTION_GRADE_PREFIX: Record<Section, string> = {
  preescolar: "K",
  primaria: "P",
};

// Matches the Grupos tab's own input cap, so a name typed here can never be
// one the school-level group editor would refuse.
const GROUP_NAME_MAX_LENGTH = 20;

interface GradeRowState {
  programIds: number[];
  // Groups are PER GRADE (SCRUM-673): real schools run one group in Kinder 1
  // and three in Kinder 3. Always at least one entry.
  groups: string[];
}

const EMPTY_GRADE_ROW: GradeRowState = {
  programIds: [],
  groups: [DEFAULT_GROUP_NAME],
};

interface SectionGradeProgramGridProps {
  setGenerationSelections: (selections: SelectionCell[]) => void;
  // OUTPUT ONLY. Groups are chosen per grade here, but SchoolToGroup stays a
  // school-wide catalogue (GroupsTab lists it; GenerarClasesModal persists it
  // additively), so the grid pushes up the UNION of every grade's groups.
  // Deliberately never read back into this component's state — that would
  // close a setGroups -> re-render -> re-derive loop.
  groups: string[];
  setGroups: (groups: string[]) => void;
  // Rehydrates selection on edit re-entry from already-created classrooms
  // (grade_id -> program_ids). Undefined/empty in creation mode — the grid
  // stays blank. Seeded at most once (see `seeded` below); never overwrites
  // subsequent user edits, even if the parent re-renders with the same value.
  initialSelection?: Record<number, number[]>;
  // Per-grade groups for the SAME rehydration pass as `initialSelection`.
  // Groups live in this component's state, so a remount loses them — and the
  // ClassesTab instance is keyed on schoolId, so CREATING the school remounts
  // it. `initialSelection` carries program_ids only, so without this every
  // grade silently reset to a single default "A", discarding typed names.
  initialGroupsByGrade?: Record<number, string[]>;
  // Active classes already created for the school — the summary splits the
  // selection into "will be created" vs "already exists" against this list,
  // mirroring the backend resolveGrid new/exists statuses client-side.
  existingClasses?: DeltaClass[];
  // Groups already persisted to the school. Their slots render as fixed
  // chips: "renaming" a persisted group cannot rename it server-side (group
  // persistence is additive-only) — it would silently create a NEW group, so
  // editing them here is a trap and is disallowed.
  existingGroups?: string[];
  // Programs the school is LICENSED for (SchoolToProgram, persisted). The
  // Materia selector is narrowed to these: a class can only exist for a
  // program the school actually bought. Empty means no licensing on record
  // yet (a school being created), which restricts nothing — that first pass
  // is what bootstraps the licensing.
  schoolProgramIds?: number[];
}

/**
 * Escuela-tab bulk selection grid: both sections (Preescolar, Primaria) are
 * always offered. Per section, pick multiple grades; per selected grade,
 * pick multiple materias (program_id). A single school-level "Grupos"
 * control (shared `groups` state) applies to every generated cell. Emits
 * one SelectionCell per selected grade x program pair, all carrying the
 * same groups list. Absorbs the old standalone "Gestión de Programas"
 * multi-select — licensing is derived elsewhere (SchoolForm/index.tsx) from
 * the emitted SelectionCell[].
 */
// Stable default identities: a `= []` parameter default re-evaluates to a
// FRESH array every render, and both values participate in effect/memo deps —
// an unstable identity there loops the cells effect (setCells → render →
// "changed" dep → setCells …) forever.
const NO_EXISTING_CLASSES: DeltaClass[] = [];
const NO_EXISTING_GROUPS: string[] = [];
const NO_INITIAL_GROUPS: Record<number, string[]> = {};
const NO_SCHOOL_PROGRAM_IDS: number[] = [];

const SectionGradeProgramGrid: React.FC<SectionGradeProgramGridProps> = ({
  setGenerationSelections,
  groups,
  setGroups,
  initialSelection,
  initialGroupsByGrade = NO_INITIAL_GROUPS,
  existingClasses = NO_EXISTING_CLASSES,
  existingGroups = NO_EXISTING_GROUPS,
  schoolProgramIds = NO_SCHOOL_PROGRAM_IDS,
}) => {
  const { grades } = useGrades();
  const { programs } = usePrograms();
  const { subjects } = useSubjects();

  // SEL (Socioemocional) is an evaluation program, not an eligible class
  // subject — it must never be selectable to generate Classrooms here.
  const excludeSubjectIds = useMemo(() => {
    const selSubjectId = subjects?.find((s) => s.code === "SEL")?.id;
    return selSubjectId != null ? [selSubjectId] : [];
  }, [subjects]);

  // Only what the school is licensed for reaches the Materia selector.
  const selectablePrograms = useMemo(
    () => restrictToSchoolPrograms(programs, schoolProgramIds),
    [programs, schoolProgramIds]
  );

  const [selectedGradeIds, setSelectedGradeIds] = useState<Record<Section, number[]>>({
    preescolar: [],
    primaria: [],
  });
  const [gradeRows, setGradeRows] = useState<Record<number, GradeRowState>>({});
  // Committed selections, derived from gradeRows (one cell per grade+program).
  const [cells, setCells] = useState<SelectionCell[]>([]);

  // Rows pending from a seed, consumed by the grade-rows-sync effect below.
  // Needed because that effect re-derives `gradeRows` from a `selectedGradeIds`
  // snapshot captured in the SAME initial effect flush as the seed effect —
  // it would otherwise run with a stale (pre-seed) `selectedGradeIds` closure
  // and wipe the freshly seeded rows before React ever commits them.
  const pendingSeedRowsRef = useRef<Record<number, GradeRowState>>({});

  // Reconstruct selection from `initialSelection` (grade_id -> program_ids
  // already materialized as classrooms) on edit re-entry. Runs at most once:
  // guarded by `seeded` so a later re-render with the same (or any)
  // `initialSelection` never clobbers a subsequent user edit. Waits for
  // `grades` to be loaded — bucketing into Preescolar/Primaria requires each
  // grade's `code`. Creation mode arrives with an empty `initialSelection`
  // and never seeds, leaving the grid blank.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded) return;
    // Grades must be loaded before we can decide anything: bucketing into
    // Preescolar/Primaria needs each grade's `code`.
    if (grades.length === 0) return;

    if (!initialSelection || Object.keys(initialSelection).length === 0) {
      // Nothing to rehydrate. Settle the seed NOW instead of staying armed:
      // in creation mode `initialSelection` is derived from THIS grid's own
      // emissions, so it turns non-empty the moment the operator picks a
      // first materia — and seeding then would rewrite selectedGradeIds from
      // the emitted cells, silently dropping every grade they had selected
      // but not yet given a materia to.
      setSeeded(true);
      return;
    }

    const nextSelectedGradeIds: Record<Section, number[]> = {
      preescolar: [],
      primaria: [],
    };
    const seedRows: Record<number, GradeRowState> = {};

    Object.entries(initialSelection).forEach(([gradeIdKey, programIds]) => {
      const gradeId = Number(gradeIdKey);
      const grade = grades.find((g) => g.id === gradeId);
      const section = (Object.keys(SECTION_GRADE_PREFIX) as Section[]).find(
        (s) => grade?.code?.startsWith(SECTION_GRADE_PREFIX[s])
      );
      if (!section) return;

      nextSelectedGradeIds[section] = [...nextSelectedGradeIds[section], gradeId];
      // Seed the grade's groups from the classes it ALREADY has, so re-entry
      // shows the real per-grade layout instead of resetting everything to a
      // single "A". A grade whose classes are all soft-deleted seeds nothing
      // and falls back to the default single group.
      // Locked (already-materialized) groups first so their persisted
      // spelling wins, then whatever the wizard carried over. Only a grade
      // with neither falls back to the single default group.
      const locked = lockedGroupsForGrade(gradeId, existingClasses);
      const carried = initialGroupsByGrade[gradeId] ?? [];
      const seededGroups = unionGroupNames({ [gradeId]: carried }, locked);
      seedRows[gradeId] = {
        programIds: [...programIds],
        groups: seededGroups.length > 0 ? seededGroups : [DEFAULT_GROUP_NAME],
      };
    });

    pendingSeedRowsRef.current = seedRows;
    setSelectedGradeIds(nextSelectedGradeIds);
    setSeeded(true);
  }, [initialSelection, grades, seeded, existingClasses, initialGroupsByGrade]);

  const gradeOptionsBySection = useMemo(() => {
    const byPrefix = (prefix: string) => grades.filter((g) => g.code?.startsWith(prefix));
    return {
      preescolar: byPrefix(SECTION_GRADE_PREFIX.preescolar),
      primaria: byPrefix(SECTION_GRADE_PREFIX.primaria),
    };
  }, [grades]);

  // Keep gradeRows in sync with the currently selected grades: initialize a
  // row for newly selected grades (consuming any pending seed row first),
  // drop rows for deselected ones. Derived state only — this effect never
  // notifies the parent.
  useEffect(() => {
    const allSelectedIds = new Set([
      ...selectedGradeIds.preescolar,
      ...selectedGradeIds.primaria,
    ]);
    setGradeRows((prev) => {
      const next: Record<number, GradeRowState> = {};
      allSelectedIds.forEach((id) => {
        // EMPTY_GRADE_ROW is a module constant: copy its `groups` ARRAY too,
        // or every freshly selected grade would share one mutable list.
        next[id] =
          prev[id] ??
          pendingSeedRowsRef.current[id] ?? {
            ...EMPTY_GRADE_ROW,
            groups: [...EMPTY_GRADE_ROW.groups],
          };
        delete pendingSeedRowsRef.current[id];
      });
      return next;
    });
  }, [selectedGradeIds]);

  // Commit the full set of cells derived from gradeRows into local state
  // only — no cross-component call from within a setState updater. Every
  // cell carries the SAME shared `groups` list (school-level, not per-grade).
  // Blank-named slots (a name input mid-edit) are excluded in real time, and
  // a new name matching a PERSISTED group case-insensitively snaps to the
  // persisted spelling: the backend builds custom_codes case-preserving, so
  // "a" next to a persisted "A" would generate a near-duplicate classroom
  // whose group never reaches the school's canonical group list.
  useEffect(() => {
    const canonicalizeGroup = (name: string): string => {
      const trimmed = name.trim();
      const persisted = existingGroups.find(
        (g) => g.trim().toLowerCase() === trimmed.toLowerCase()
      );
      return persisted ? persisted.trim() : trimmed;
    };
    const nextCells: SelectionCell[] = [];
    Object.entries(gradeRows).forEach(([gradeIdKey, row]) => {
      if (row.programIds.length === 0) return;
      // Each grade contributes ITS OWN groups — this is the whole point of
      // SCRUM-673. Duplicates within a grade are collapsed because the
      // backend rejects a repeated group name for the same grade_id.
      const effectiveGroups = Array.from(
        new Set(row.groups.map(canonicalizeGroup).filter((g) => g !== ""))
      );
      if (effectiveGroups.length === 0) return;
      const gradeId = Number(gradeIdKey);
      row.programIds.forEach((programId) => {
        nextCells.push({
          grade_id: gradeId,
          program_id: programId,
          groups: effectiveGroups,
        });
      });
    });
    setCells(nextCells);
  }, [gradeRows, existingGroups]);

  // The school-level catalogue is what the grades use, unioned with what the
  // school ALREADY has persisted. The persisted half is not optional: this
  // list is the same state GroupsTab renders, so emitting only the grid's own
  // groups would blank the school's catalogue every time the grid mounts with
  // an empty selection in edit mode. Derived from gradeRows/existingGroups
  // only — never from the `groups` prop — so it cannot feed back into this
  // component's state.
  //
  // SCRUM-879: a name seeded from an existing class is NOT evidence that the
  // school still has that group. Deleting a group never touched its
  // classrooms, so a class can outlive its catalogue entry — and emitting
  // that name here would show a group the backend does not have in the list
  // GroupsTab renders, where deleting it answers 404. Seeded names therefore
  // only reach the catalogue once it actually holds them; names the operator
  // typed always do.
  useEffect(() => {
    const persistedKeys = new Set(
      existingGroups.map((name) => name.trim().toLowerCase())
    );
    const byGrade: Record<number, string[]> = {};
    Object.entries(gradeRows).forEach(([gradeIdKey, row]) => {
      const gradeId = Number(gradeIdKey);
      const seededFromClasses = new Set(
        lockedGroupsForGrade(gradeId, existingClasses).map((name) =>
          name.trim().toLowerCase()
        )
      );
      byGrade[gradeId] = row.groups.filter((name) => {
        const key = name.trim().toLowerCase();
        return persistedKeys.has(key) || !seededFromClasses.has(key);
      });
    });
    setGroups(unionGroupNames(byGrade, existingGroups));
    // setGroups is a state setter identity from the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradeRows, existingGroups, existingClasses]);

  // Notify the parent as a dedicated effect keyed on the committed cells,
  // never from inside the setCells/setGradeRows updaters themselves.
  // While a rehydration seed is pending (fresh mount with a non-empty
  // initialSelection), the pre-seed empty cells are suppressed: emitting []
  // would transiently wipe the parent's carried selections (and flash an
  // all-zero preview in the auto-opened generation modal after creation).
  const seedSettledRef = useRef(false);
  useEffect(() => {
    const seedPending =
      Boolean(initialSelection && Object.keys(initialSelection).length > 0) &&
      !seedSettledRef.current;
    if (seedPending) {
      if (cells.length === 0) return;
      seedSettledRef.current = true;
    }
    setGenerationSelections(cells);
    // setGenerationSelections is a state setter identity from the parent;
    // initialSelection only matters until the seed settles (ref-guarded).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cells]);

  // Honest summary: split the selection into combos that would actually be
  // created vs combos already covered by an active class, instead of the old
  // blind cells x groups product that "promised" classes that already existed.
  const generationDelta = useMemo(
    () => countGenerationDelta(cells, existingClasses),
    [cells, existingClasses]
  );

  // Selected grades in reading order (Preescolar then Primaria) — the "Grupos
  // por grado" list mirrors the section order above it, so a grade never
  // appears in a different place in the two blocks.
  const orderedSelectedGrades = useMemo(() => {
    const toEntries = (ids: number[]) =>
      ids.map((gradeId) => ({
        gradeId,
        gradeLabel:
          grades.find((g) => g.id === gradeId)?.grade ?? String(gradeId),
      }));
    return [
      ...toEntries(selectedGradeIds.preescolar),
      ...toEntries(selectedGradeIds.primaria),
    ];
  }, [selectedGradeIds, grades]);

  const handleSectionGradesChange = (section: Section, gradeIds: number[]) => {
    setSelectedGradeIds((prev) => ({ ...prev, [section]: gradeIds }));
  };

  // `values` may carry the ALL_SUBJECTS_VALUE sentinel from the "Todas" row;
  // the toggle resolves it against the row's PREVIOUS selection, so it must be
  // applied inside the updater rather than from a possibly stale render read.
  const handleGradeProgramsChange = (
    gradeId: number,
    values: number[],
    allProgramIds: number[]
  ) => {
    setGradeRows((prev) => ({
      ...prev,
      [gradeId]: {
        ...prev[gradeId],
        programIds: resolveAllSubjectsToggle(
          values,
          allProgramIds,
          prev[gradeId]?.programIds ?? []
        ),
      },
    }));
  };

  const updateGradeGroups = (
    gradeId: number,
    update: (groups: string[]) => string[]
  ) => {
    setGradeRows((prev) => {
      const row = prev[gradeId];
      if (!row) return prev;
      return { ...prev, [gradeId]: { ...row, groups: update(row.groups) } };
    });
  };

  const handleAddGroup = (gradeId: number) =>
    updateGradeGroups(gradeId, (current) => [
      ...current,
      nextGroupName(current, existingGroups),
    ]);

  const handleGroupNameChange = (
    gradeId: number,
    index: number,
    value: string
  ) =>
    updateGradeGroups(gradeId, (current) =>
      current.map((name, i) => (i === index ? value : name))
    );

  // "Cada grado debe tener al menos un grupo" — the last slot has no remove
  // control at all, so this guard is a backstop, not the only enforcement.
  const handleRemoveGroup = (gradeId: number, index: number) =>
    updateGradeGroups(gradeId, (current) =>
      current.length <= 1 ? current : current.filter((_, i) => i !== index)
    );

  const renderSection = (section: Section) => {
    const gradeOptions = gradeOptionsBySection[section];
    const selectedIds = selectedGradeIds[section];

    return (
      <div key={section} className="sectionContainer" style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginBottom: 15 }}>
          {SECTION_LABEL[section]}
        </Title>

        <label htmlFor={`grades-select-${section}`}>
          {`Grados — ${SECTION_LABEL[section]}`}
        </label>
        <Select
          id={`grades-select-${section}`}
          mode="multiple"
          placeholder="Selecciona los grados"
          value={selectedIds}
          onChange={(value: number[]) => handleSectionGradesChange(section, value)}
          style={{ width: "100%" }}
          options={gradeOptions.map((g) => ({ value: g.id, label: g.grade }))}
        />

        {selectedIds.map((gradeId) => {
          const grade = grades.find((g) => g.id === gradeId);
          const gradeLabel = grade?.grade ?? String(gradeId);
          const row = gradeRows[gradeId] ?? EMPTY_GRADE_ROW;
          const subjectOptions = buildSubjectOptions(
            selectablePrograms,
            gradeId,
            excludeSubjectIds
          );
          const allProgramIds = subjectOptions.map((o) => o.value);
          const selectedCount = allProgramIds.filter((id) =>
            row.programIds.includes(id)
          ).length;
          const allSelected = areAllSubjectsSelected(allProgramIds, row.programIds);
          // The sentinel is an OPTION only — it is deliberately kept out of
          // `value`, so it never renders as a tag in the input and never has to
          // be stripped from the emitted cells.
          const materiaOptions =
            allProgramIds.length > 0
              ? [{ value: ALL_SUBJECTS_VALUE, label: "Todas" }, ...subjectOptions]
              : subjectOptions;

          return (
            <Row gutter={16} key={gradeId} style={{ marginTop: 12 }}>
              <Col span={4}>
                <Text strong>{gradeLabel}</Text>
              </Col>

              <Col span={20}>
                <label htmlFor={`subject-select-${gradeId}`}>
                  {`Asignatura — ${gradeLabel}`}
                </label>
                <Select
                  id={`subject-select-${gradeId}`}
                  mode="multiple"
                  placeholder="Selecciona las asignaturas"
                  value={row.programIds}
                  onChange={(value: number[]) =>
                    handleGradeProgramsChange(gradeId, value, allProgramIds)
                  }
                  style={{ width: "100%" }}
                  options={materiaOptions}
                  optionRender={(option) =>
                    option.value === ALL_SUBJECTS_VALUE ? (
                      <AllSubjectsOption
                        checked={allSelected}
                        selectedCount={selectedCount}
                        total={allProgramIds.length}
                      />
                    ) : (
                      option.label
                    )
                  }
                />
              </Col>
            </Row>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <Title level={5} style={{ marginBottom: 15 }}>
        Grados y Programas
      </Title>

      {renderSection("preescolar")}
      {renderSection("primaria")}

      {/* Groups come AFTER grades and materias (SCRUM-673): you cannot say how
          many groups a grade runs before you have picked the grade. */}
      <div className="groupsControl" style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginBottom: 4 }}>
          Grupos por grado
        </Title>
        <Text type="secondary" style={{ display: "block", marginBottom: 15 }}>
          Puedes añadir más grupos y eliminar los que agregaste por error. Cada
          grado debe tener al menos un grupo.
        </Text>

        {orderedSelectedGrades.length === 0 ? (
          <Text type="secondary">
            Selecciona al menos un grado para configurar sus grupos.
          </Text>
        ) : (
          orderedSelectedGrades.map(({ gradeId, gradeLabel }) => {
            const row = gradeRows[gradeId] ?? EMPTY_GRADE_ROW;
            const locked = lockedGroupsForGrade(gradeId, existingClasses);
            const lockedKeys = new Set(
              locked.map((name) => name.trim().toLowerCase())
            );

            return (
              <Row
                gutter={16}
                key={gradeId}
                align="middle"
                style={{ marginBottom: 12 }}
              >
                <Col span={4}>
                  <Text strong>{gradeLabel}</Text>
                </Col>
                <Col span={20}>
                  <Space wrap>
                    {row.groups.map((name, index) => {
                      // A group whose classes already exist cannot be renamed
                      // here: group persistence is additive-only, so renaming
                      // would mint a NEW group and leave the original's
                      // classrooms orphaned.
                      const isLocked = lockedKeys.has(name.trim().toLowerCase());
                      if (isLocked) {
                        return (
                          <Tag
                            key={index}
                            aria-label={`Grupo existente ${name} de ${gradeLabel}`}
                            style={{ fontSize: 14, padding: "4px 10px" }}
                          >
                            {name}
                          </Tag>
                        );
                      }
                      return (
                        <Input
                          key={index}
                          aria-label={`Grupo ${index + 1} de ${gradeLabel}`}
                          value={name}
                          maxLength={GROUP_NAME_MAX_LENGTH}
                          onChange={(e) =>
                            handleGroupNameChange(gradeId, index, e.target.value)
                          }
                          style={{ width: 160 }}
                          suffix={
                            row.groups.length > 1 ? (
                              <CloseOutlined
                                role="button"
                                aria-label={`Eliminar grupo ${index + 1} de ${gradeLabel}`}
                                onClick={() => handleRemoveGroup(gradeId, index)}
                                style={{ cursor: "pointer", color: "#8c8c8c" }}
                              />
                            ) : undefined
                          }
                        />
                      );
                    })}
                    <Button
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={() => handleAddGroup(gradeId)}
                    >
                      {`Añadir grupo a ${gradeLabel}`}
                    </Button>
                  </Space>
                </Col>
              </Row>
            );
          })
        )}
      </div>

      <GenerationSummary
        newCount={generationDelta.newCount}
        existingCount={generationDelta.existingCount}
      />
    </div>
  );
};

/**
 * The "Todas" dropdown row. The checkbox is a state indicator, not an
 * independent control — the Select option owns the click, so pointer events
 * are disabled on it to keep a single toggle path.
 */
const AllSubjectsOption: React.FC<{
  checked: boolean;
  selectedCount: number;
  total: number;
}> = ({ checked, selectedCount, total }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    }}
  >
    <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
      <Checkbox
        checked={checked}
        indeterminate={!checked && selectedCount > 0}
        style={{ pointerEvents: "none" }}
      />
      Todas
    </span>
    <Text type="secondary">{`${selectedCount}/${total}`}</Text>
  </div>
);

const GenerationSummary: React.FC<{
  newCount: number;
  existingCount: number;
}> = ({ newCount, existingCount }) => {
  if (newCount === 0 && existingCount > 0) {
    return (
      <Text strong>
        Todo lo seleccionado ya existe — no hay clases nuevas para generar
      </Text>
    );
  }

  const newText =
    newCount === 1
      ? "Se creará 1 clase nueva"
      : `Se crearán ${newCount} clases nuevas`;
  const existingText =
    existingCount === 1
      ? "1 ya existe y no se modificará"
      : `${existingCount} ya existen y no se modificarán`;

  return (
    <Text strong>
      {newText}
      {existingCount > 0 && (
        <Text type="secondary"> · {existingText}</Text>
      )}
    </Text>
  );
};

export default SectionGradeProgramGrid;
